import { FC, useEffect, useMemo } from "react";

import {
  CommunicationPreferenceFormType,
  FormCategory,
  FormType,
  hasAddOnRates,
  parseFormBackgroundTheme,
  TicketingFormCategory,
} from "@simplyk/common";
import dynamic from "next/dynamic";

import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FormStepKey } from "@/canvas/features/FormV2/steps/FormStepKey";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { AuctionStep1 } from "@/canvas/features/FormV2/steps/step1/Auction/AuctionStep1";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingFrontendPageOutput } from "@/common/types/trpc";
import { DeviceSwitchablePreviewLayout } from "@/components/LiveEditorElements/LivePreview/DeviceSwitchablePreviewLayout";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { PreviewLocaleProvider } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewLocaleProvider";
import {
  useCommunicationPreferencesForPreview,
  withPreviewQuestionTitle,
} from "@/features/LiveFormEditor/LivePreview/useCommunicationPreferencesForPreview";
import { LiveEditorStepKey } from "@/features/LiveFormEditor/Steps/LiveEditorStep.enum";
import { useTicketingTaxes } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useTicketingTaxes";

const FrontendPreviewProvider = dynamic(
  () => import("../contexts/TicketingFrontendPreviewProvider").then((mod) => mod.TicketingFrontendPreviewProvider),
  {
    ssr: false,
  }
);

const PreviewProvider = dynamic(() => import("../contexts/PreviewContext").then((mod) => mod.PreviewProvider), {
  ssr: false,
});

const FormProviders = dynamic(() => import("@/canvas/features/FormV2/FormProviders").then((mod) => mod.FormProviders), {
  ssr: false,
});

const FormLayout = dynamic(() => import("@/canvas/features/FormV2/FormLayout").then((mod) => mod.FormLayout), {
  ssr: false,
});

const TicketingStep1 = dynamic(
  () => import("@/canvas/features/FormV2/steps/step1/Ticketing/TicketingStep1").then((mod) => mod.TicketingStep1),
  {
    ssr: false,
  }
);

const FormV2Step2 = dynamic(
  () => import("@/canvas/features/FormV2/steps/step2/FormV2Step2").then((mod) => mod.FormV2Step2),
  {
    ssr: false,
  }
);

const TicketingAddOnsStep = dynamic(
  () => import("@/canvas/features/FormV2/steps/addOns/TicketingAddOnsStep").then((mod) => mod.TicketingAddOnsStep),
  {
    ssr: false,
  }
);

export const TicketingLivePreview: FC = () => {
  const { t } = useTranslate();
  const {
    stepProgress,
    currentStepKey,
    currentStepIndex,
    liveFormData,
    liveEditorStepsKeys,
    getKeyStepFromIndex,
    formCategory,
    setPreviewStepKey,
    previewStepKey,
    previewDevice,
    setPreviewDevice,
    setPreviewScale,
    iframeRef,
    isOnboarding,
    previewPrizeFocusId,
    apiFormObject: apiTicketing,
  } = useLiveFormEditorContext<"ticketing">();

  const formHasAddOnRates = hasAddOnRates(liveFormData?.ticketing?.rates);

  const showsAddOnsStep = formHasAddOnRates && liveFormData?.addOnsPreviewSection !== "additionalDonation";

  useEffect(() => {
    const previewStepKey = getPreviewStepKey(currentStepKey, showsAddOnsStep, liveFormData?.lastChangedAdvancedField);
    setPreviewStepKey(previewStepKey);
  }, [currentStepKey, showsAddOnsStep, liveFormData?.lastChangedAdvancedField, setPreviewStepKey]);

  const { taxes: organizationTaxes } = useTicketingTaxes();
  const { organization: currentOrganization } = useCurrentUserContext();
  const sponsorTaxId = currentOrganization?.charityNumber ?? null;
  const sponsorPhoneNumber = currentOrganization?.phoneNumber ?? null;

  const { communicationPreferences, questionTitle } = useCommunicationPreferencesForPreview(
    apiTicketing.id,
    CommunicationPreferenceFormType.Ticketing
  );

  const ticketing = useMemo(() => {
    const previewTicketing = liveFormData?.ticketing && {
      ...liveFormData.ticketing,
      communicationPreferences,
      ticketingFields: withPreviewQuestionTitle(
        liveFormData.ticketing.ticketingFields,
        apiTicketing.locale,
        questionTitle
      ),
      sweepstakesSponsor:
        liveFormData.ticketing.formCategory === TicketingFormCategory.Sweepstakes
          ? { taxId: sponsorTaxId, phoneNumber: sponsorPhoneNumber }
          : null,
    };
    if (!previewTicketing?.rates?.length || organizationTaxes.length === 0) {
      return previewTicketing;
    }
    const taxById = new Map(organizationTaxes.map((tax) => [tax.id, tax]));
    return {
      ...previewTicketing,
      rates: previewTicketing.rates.map((rate) => {
        if (rate.taxes?.length || !rate.taxIds?.length) {
          return rate;
        }
        const taxes = rate.taxIds.flatMap((taxId) => {
          const tax = taxById.get(taxId);
          return tax ? [{ id: tax.id, name: tax.name, ratePpm: tax.ratePpm }] : [];
        });
        return taxes.length > 0 ? { ...rate, taxes } : rate;
      }),
    };
  }, [
    liveFormData?.ticketing,
    organizationTaxes,
    communicationPreferences,
    apiTicketing.locale,
    questionTitle,
    sponsorTaxId,
    sponsorPhoneNumber,
  ]);

  if (!liveFormData || !ticketing) {
    return null;
  }

  const { occurrencesWithRates, selectedOccurrenceWithRates } = liveFormData;

  const isAuction = formCategory === FormCategory.Auction;

  return (
    !!liveFormData && (
      <DeviceSwitchablePreviewLayout
        currentStepIndex={currentStepIndex}
        previewDevice={previewDevice}
        setPreviewDevice={setPreviewDevice}
        setPreviewScale={setPreviewScale}
        iframeRef={iframeRef}
        hasPreviewPadding={previewStepKey === "payment"}
        showConfetti={currentStepKey === "review"}
        slots={{
          content: (
            <PreviewLocaleProvider locale={apiTicketing.locale}>
              <FrontendPreviewProvider
                ticketing={ticketing as TicketingFrontendPageOutput}
                organization={ticketing.organization!}
              >
                <PreviewProvider
                  iframeRef={iframeRef}
                  advancedSettings={{
                    dafEnabled: false,
                    chequeEnabled: typeof ticketing.chequeMinimumAmount === "number",
                    allowInHonour: false,
                  }}
                  isPreview={true}
                  furthestStepReachedIndex={stepProgress.reached}
                  placeholders={liveFormData.placeholders}
                  furthestStepReachedKey={getKeyStepFromIndex(stepProgress.reached)}
                  currentStepKey={currentStepKey}
                  liveEditorStepsKeys={liveEditorStepsKeys}
                  previewAmountFocus={null}
                >
                  <FormProviders
                    type={FormType.Ticketing}
                    formObject={ticketing as TicketingFrontendPageOutput}
                    isEmbed={false}
                    previewTicketingData={{
                      occurrencesWithRates,
                      selectedOccurrenceWithRates: selectedOccurrenceWithRates ?? null,
                      focusedPrizeId: currentStepKey === "prizes" ? previewPrizeFocusId : null,
                    }}
                  >
                    <FormLayout
                      step={previewStepKey}
                      slots={{
                        selectionStep: isAuction ? <AuctionStep1 /> : <TicketingStep1 />,
                        addOnsStep: <TicketingAddOnsStep />,
                        paymentStep: <FormV2Step2 />,
                        languageSelect: null,
                        closeEmbedButton: null,
                        thankYou: null,
                      }}
                      backgroundTheme={parseFormBackgroundTheme(liveFormData.ticketing.backgroundTheme)}
                      brandColorHex={liveFormData.ticketing.color}
                    />
                  </FormProviders>
                </PreviewProvider>
              </FrontendPreviewProvider>
            </PreviewLocaleProvider>
          ),
          footerHint: isOnboarding ? (
            <DashboardTypography variant="body1" vibe="text-neutral-quiet" marginTop={3}>
              {t("dashboard", "allChangesCanBeEditedLater")}
            </DashboardTypography>
          ) : undefined,
        }}
      />
    )
  );
};

const getPreviewStepKey = (
  currentStepKey: LiveEditorStepKey,
  showsAddOnsStep: boolean,
  lastChangedAdvancedField?: string
): FormStepKey => {
  if (currentStepKey === "advanced-settings" && lastChangedAdvancedField === "chequeEnabled") {
    return "payment";
  }
  if (
    [
      "buyer-questions",
      "questions",
      "discount-codes",
      "thank-you-email",
      "business-model",
      "order-fulfillment",
    ].includes(currentStepKey)
  ) {
    return "payment";
  }
  if (currentStepKey === "add-ons" && showsAddOnsStep) {
    return "addOns";
  }
  return "selection";
};
